/**
 * Dinheiro em real, tratado como INTEIRO DE CENTAVOS.
 *
 * O banco guarda dinheiro em numeric(14,2) e o driver o entrega como string,
 * de proposito: converter para float perderia centavo em valor grande. Aqui a
 * regra e a mesma — nunca somar nem arredondar dinheiro como numero de ponto
 * flutuante. Toda conta e feita em centavos inteiros; o real so aparece na
 * borda, para exibir ou para enviar ao servidor.
 *
 * CLAUDE.md, "Regras de banco", item 7; docs/DESIGN-SYSTEM.md, secao 7.
 */

const formatador = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Converte o valor recebido em CENTAVOS inteiros.
 *
 * Aceita a string do banco ("1234.56"), a string do usuario ("1.234,56") e um
 * numero em reais. Nao aceita float com erro embutido: quando vem numero, ele
 * e arredondado ao centavo mais proximo uma unica vez, na entrada.
 *
 * Devolve null para entrada que nao e dinheiro — quem chama decide o que fazer,
 * em vez de receber NaN silencioso.
 */
export function paraCentavos(valor: string | number | null | undefined): number | null {
  if (valor === null || valor === undefined || valor === '') {
    return null;
  }

  if (typeof valor === 'number') {
    if (!Number.isFinite(valor)) {
      return null;
    }
    return Math.round(valor * 100);
  }

  const bruto = valor.trim();

  // Formato do banco: ponto decimal, sem separador de milhar ("1234.56").
  if (/^-?\d+(\.\d+)?$/.test(bruto)) {
    return arredondarCentavos(bruto);
  }

  // Formato do usuario: ponto de milhar, virgula decimal ("1.234,56").
  const limpo = bruto.replace(/\s/g, '').replace(/\./g, '').replace(',', '.');
  if (/^-?\d+(\.\d+)?$/.test(limpo)) {
    return arredondarCentavos(limpo);
  }

  return null;
}

/**
 * Arredonda uma string decimal ao centavo, sem passar por float. Trabalha nos
 * digitos: pega dois depois do ponto e arredonda pelo terceiro.
 */
function arredondarCentavos(decimal: string): number {
  const negativo = decimal.startsWith('-');
  const semSinal = negativo ? decimal.slice(1) : decimal;
  const [inteiro = '0', fracao = ''] = semSinal.split('.');

  const centavosStr = (fracao + '00').slice(0, 2);
  let centavos = Number(inteiro) * 100 + Number(centavosStr);

  const terceiro = fracao.charAt(2);
  if (terceiro && Number(terceiro) >= 5) {
    centavos += 1;
  }

  return negativo ? -centavos : centavos;
}

/** Centavos inteiros -> "R$ 1.234,56". */
export function formatarCentavos(centavos: number | null | undefined): string {
  if (centavos === null || centavos === undefined || !Number.isFinite(centavos)) {
    return formatador.format(0);
  }
  return formatador.format(centavos / 100);
}

/** Formata o que vier, passando por centavos. Vazio nao vira "R$ 0,00" a esmo. */
export function formatarDinheiro(valor: string | number | null | undefined, vazio = '—'): string {
  const centavos = paraCentavos(valor);
  if (centavos === null) {
    return vazio;
  }
  return formatarCentavos(centavos);
}

/** Centavos -> string canonica do banco ("1234.56"), para enviar ao servidor. */
export function centavosParaDecimal(centavos: number | null | undefined): string {
  if (centavos === null || centavos === undefined || !Number.isFinite(centavos)) {
    return '';
  }
  const negativo = centavos < 0;
  const abs = Math.abs(Math.trunc(centavos));
  const inteiro = Math.trunc(abs / 100);
  const resto = String(abs % 100).padStart(2, '0');
  return `${negativo ? '-' : ''}${inteiro}.${resto}`;
}
